import type { StoredSession } from '~/gql/sessions/sessions'
import type { EditableSession } from '~/pages/backoffice/admin/sessions/editable-session'
import { useMutation, useQuery } from '@apollo/client/react'
import { TrainerCapability } from '@zeus/index'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader } from '~/components/ui/card'
import { GQL_GROUPS_FOR_FILTER } from '~/gql/groups/groups'

import { GQL_DELETE_SESSION, GQL_SESSIONS } from '~/gql/sessions/sessions'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useCan } from '~/hooks/use-can'
import SessionDeleteDialog from '~/pages/backoffice/admin/sessions/components/SessionDeleteDialog'
import SessionModal from '~/pages/backoffice/admin/sessions/components/SessionModal'
import SessionsList from '~/pages/backoffice/admin/sessions/components/SessionsList'
import SessionsToolbar from '~/pages/backoffice/admin/sessions/components/SessionsToolbar'
import { useSessionFilters } from '~/pages/backoffice/admin/sessions/use-session-filters'
import { useSessionFormOptions } from '~/pages/backoffice/admin/sessions/use-session-form-options'
import { NoResults } from '~/pages/backoffice/NoResults'
import ResultsStatus from '~/pages/backoffice/ResultsStatus'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'

/**
 * Les parcours de l'organisation. Créer, modifier, supprimer et ouvrir un
 * module n'apparaissent qu'à qui en a le droit (#11) ; les modules, comptes et
 * groupes ne servent qu'au formulaire, et ne sont lus que si on peut l'ouvrir.
 */
function SessionsTable() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canManage = useCan(TrainerCapability.SESSIONS_MANAGE)
  const canDelete = useCan(TrainerCapability.SESSIONS_DELETE)
  const canOpenModule = useCan(TrainerCapability.MODULES_VIEW)
  const { data, loading, refetch } = useQuery(GQL_SESSIONS, { fetchPolicy: 'cache-and-network' })
  const { readyModules, users, groups, certificateTemplates } = useSessionFormOptions(canManage)
  const [deleteSession] = useMutation(GQL_DELETE_SESSION)

  const [openModal, setOpenModal] = useState(false)
  const [editing, setEditing] = useState<EditableSession | undefined>()
  const [removing, setRemoving] = useState<StoredSession | null>(null)
  const sessions = useMemo(() => data?.sessions ?? [], [data])
  // Les groupes lisent des comptes : sans le droit de voir les apprenants (#11),
  // pas de filtre par groupe — plutôt qu'une requête refusée.
  const canSeeLearners = useCan(TrainerCapability.LEARNERS_VIEW)
  const { data: groupsData } = useQuery(GQL_GROUPS_FOR_FILTER, { skip: !canSeeLearners })
  const filterGroups = groupsData?.groups ?? []
  const filters = useSessionFilters(sessions, filterGroups)
  const { pageRows, pagination } = useClientPagination(filters.visible, filters.filtersKey)

  const openCreate = () => {
    setEditing(undefined)
    setOpenModal(true)
  }

  const openEdit = (session: EditableSession) => {
    setEditing(session)
    setOpenModal(true)
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteSession({ variables: { id } })
      toast.success(t('sessions.deleted'))
      await refetch()
    } catch {
      toast.error(t('sessions.deleteFailed'))
    }
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <Card {...tourAnchor('sessions.list')}>
        {/* Le titre de l'écran est porté par le layout (`BackOfficeLayout`) :
            le répéter ici ne faisait qu'afficher deux fois le même mot. */}
        <CardHeader>
          <SessionsToolbar
            filters={filters}
            groups={filterGroups}
            actions={canManage && (
              <Button onClick={openCreate} {...tourAnchor('sessions.new')}>
                <Plus className="mr-2 size-4" />
                {t('sessions.new')}
              </Button>
            )}
          />
          <ResultsStatus
            count={filters.visible.length}
            filtering={filters.query !== '' || filters.status !== '' || filters.moduleId !== '' || filters.groupId !== '' || filters.tagId !== '' || filters.trainerId !== ''}
          />
        </CardHeader>
        <CardContent>
          {loading && sessions.length === 0
            ? <div className="flex justify-center p-6"><Spinner className="size-6" /></div>
            : sessions.length === 0
              ? <p className="p-4 text-center text-sm text-muted-foreground">{t('sessions.empty')}</p>
              : filters.visible.length === 0
                ? <NoResults query={filters.query} onReset={filters.reset} />
                : (
                    <>
                      <SessionsList
                        sessions={pageRows}
                        onEdit={canManage ? openEdit : undefined}
                        onDelete={canDelete ? setRemoving : undefined}
                        onOpenModule={canOpenModule ? id => navigate(`/scorm/preview/${id}`) : undefined}
                      />
                      <TablePagination {...pagination} />
                    </>
                  )}
        </CardContent>
      </Card>

      {removing && (
        <SessionDeleteDialog
          session={removing}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            void handleDelete(removing.id)
            setRemoving(null)
          }}
        />
      )}

      {openModal && (
        <SessionModal
          open={openModal}
          onCancel={() => { setOpenModal(false); setEditing(undefined) }}
          onSuccess={refetch}
          modules={readyModules}
          users={users}
          groups={groups}
          certificateTemplates={certificateTemplates}
          session={editing}
          tagSuggestions={filters.tagOptions.map(tag => tag.name)}
        />
      )}
    </div>
  )
}

export default SessionsTable
